import React, { Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntApp, ConfigProvider } from 'antd';
import koKR from 'antd/locale/ko_KR';
import enUS from 'antd/locale/en_US';
import './theme/tokens.css';
import './styles/console.css';
import './styles/marketing.css';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RouteFallback from './components/RouteFallback';
import {
  LazyAppLayout,
  LazyDashboard,
  LazyDocsApiAndSdk,
  LazyDocsDevelopersHub,
  LazyDocsEdgeAndCloud,
  LazyDocsProductLines,
  LazyDocsVisionAndAgent,
  LazyDocsVmsIotIntegration,
  LazyLandingPage,
  LazyLoginForm,
  LazyFusionWorkspace,
  LazyModelTraining,
  LazyOnboardingPage,
  LazyParkwaveAmPage,
  LazyParkwaveApplicationHub,
  LazyParkwaveIndustrialReports,
  LazyParkwaveMilitaryPage,
  LazyParkwaveParkingPage,
  LazyParkwaveRootEntry,
  LazyParkwaveSiteNotFound,
  LazySolutionAutoAm,
  LazySolutionAutoArms,
  LazySolutionAutoInfra,
  LazySolutionAutoPark,
} from './routes/lazyPages';
import { PARKWAVE_SITE } from './config/parkwave';
import { enterpriseTheme } from './theme/enterprise';
import ErrorBoundary from './components/ErrorBoundary';
import AppToastInit from './feedback/AppToastInit';
import LangQuerySync from './i18n/LangQuerySync';
import { applyDocumentMeta, useLocale } from './i18n/locale';

const withSuspense = (node: React.ReactNode) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const sharedConsoleRoutes = (
  <>
    <Route path="/try" element={withSuspense(<LazyLandingPage />)} />
    <Route path="/login" element={withSuspense(<LazyLoginForm />)} />
    <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
    <Route path="/model-training" element={<Navigate to="/app/model-training" replace />} />
    <Route
      path="/app"
      element={
        <ProtectedRoute>
          {withSuspense(<LazyAppLayout />)}
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="start" element={withSuspense(<LazyOnboardingPage />)} />
      <Route path="dashboard" element={withSuspense(<LazyDashboard />)} />
      <Route path="fusion" element={withSuspense(<LazyFusionWorkspace />)} />
      <Route path="model-training" element={withSuspense(<LazyModelTraining />)} />
      <Route path="*" element={<Navigate to="dashboard" replace />} />
    </Route>
  </>
);

const docsRoutes = (
  <>
    <Route path="/docs" element={withSuspense(<LazyDocsDevelopersHub />)} />
    <Route path="/docs/product-lines" element={withSuspense(<LazyDocsProductLines />)} />
    <Route path="/docs/vision-and-agent" element={withSuspense(<LazyDocsVisionAndAgent />)} />
    <Route path="/docs/vms-iot-integration" element={withSuspense(<LazyDocsVmsIotIntegration />)} />
    <Route path="/docs/edge-and-cloud" element={withSuspense(<LazyDocsEdgeAndCloud />)} />
    <Route path="/docs/api-and-sdk" element={withSuspense(<LazyDocsApiAndSdk />)} />
  </>
);

const parkwaveProductRoutes = (
  <>
    <Route path="/parkwave/application" element={withSuspense(<LazyParkwaveApplicationHub />)} />
    <Route path="/parkwave/industrial-reports" element={withSuspense(<LazyParkwaveIndustrialReports />)} />
    <Route path="/parkwave/parking" element={withSuspense(<LazyParkwaveParkingPage />)} />
    <Route path="/parkwave/am" element={withSuspense(<LazyParkwaveAmPage />)} />
    <Route path="/parkwave/military" element={withSuspense(<LazyParkwaveMilitaryPage />)} />
  </>
);

const FullRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={withSuspense(<LazyParkwaveRootEntry />)} />
    {parkwaveProductRoutes}
    <Route path="/solutions/auto-park" element={withSuspense(<LazySolutionAutoPark />)} />
    <Route path="/solutions/auto-am" element={withSuspense(<LazySolutionAutoAm />)} />
    <Route path="/solutions/auto-arms" element={withSuspense(<LazySolutionAutoArms />)} />
    <Route path="/solutions/auto-infra" element={withSuspense(<LazySolutionAutoInfra />)} />
    {docsRoutes}
    {sharedConsoleRoutes}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

/** Vercel 프로젝트별 최소 라우트 — 마케팅 일부는 전용 허브로 리다이렉트, 개발자 문서는 전부 유지 */
const MinimalRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={withSuspense(<LazyParkwaveRootEntry />)} />
    {parkwaveProductRoutes}
    {sharedConsoleRoutes}
    {docsRoutes}
    <Route path="/solutions/auto-park" element={<Navigate to="/parkwave/parking" replace />} />
    <Route path="/solutions/auto-am" element={<Navigate to="/parkwave/am" replace />} />
    <Route path="/solutions/auto-arms" element={<Navigate to="/parkwave/military" replace />} />
    <Route path="/solutions/auto-infra" element={withSuspense(<LazySolutionAutoInfra />)} />
    <Route path="*" element={withSuspense(<LazyParkwaveSiteNotFound />)} />
  </Routes>
);

const App: React.FC = () => {
  const locale = useLocale();
  const antdLocale = useMemo(() => (locale === 'en' ? enUS : koKR), [locale]);

  React.useEffect(() => {
    applyDocumentMeta(locale);
  }, [locale]);

  return (
    <ConfigProvider locale={antdLocale} theme={enterpriseTheme}>
      <AntApp>
        <AppToastInit />
        <AuthProvider>
          <Router>
            <LangQuerySync />
            <ErrorBoundary>
              {PARKWAVE_SITE === 'full' ? <FullRoutes /> : <MinimalRoutes />}
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
